import { buildNewProfilesRequest } from '../../../../services/personApi';
import type { FinancialRepresentative } from '../../../../services/personApi';
import { cleanTaxId } from '../../../../utils/taxIdUtils';
import { sameFinancialRepresentativeIds, toFinancialRepresentativeIds } from './financialRepresentatives';

export const COMPLETED_REGISTRATION_STATUS = 'COMPLETED';
export const SAVED_STATUS_NOT_COMPLETED_REASON = 'Disponível quando o Status de Cadastro salvo for Concluída.';
export const UNSAVED_CHANGES_REASON = 'Salve as alterações antes de notificar.';

export interface NotifyButtonState {
    enabled: boolean;
    reason: string;
}

export interface BasicDataForm {
    id: string;
    personId: string;
    govTaxId: string;
    legalName: string;
    fantasyName: string;
    zipcode: string;
    streetType: string;
    street: string;
    number: string;
    complement: string;
    district: string;
    cityId: string;
    nire?: string;
    registrationStatus?: string;
    financialRepresentatives?: FinancialRepresentative[];
    financialRepresentativesLoaded?: boolean;
}

export interface BasicDataPayload {
    id: string;
    uuid: string;
    govTaxId: string;
    legalName: string;
    fantasyName: string;
    zipcode: string;
    streetType: string;
    street: string;
    number: string;
    complement: string;
    district: string;
    cityId: string;
    nire: string;
    registrationStatus: string;
    financialRepresentativeIds?: string[];
}

const withoutFinancialRepresentativeIds = (payload: BasicDataPayload | null): BasicDataPayload | null => {
    if (payload === null) return null;
    return { ...payload, financialRepresentativeIds: undefined };
};

const financialRepresentativesChanged = (saved: BasicDataPayload | null, current: BasicDataPayload): boolean => {
    if (!saved?.financialRepresentativeIds || !current.financialRepresentativeIds) return false;
    return !sameFinancialRepresentativeIds(saved.financialRepresentativeIds, current.financialRepresentativeIds);
};

/**
 * Body of the PUT /legal-persons/{id} sent by "Atualizar" (and by "Salvar" of Aval. varejo).
 * financialRepresentativeIds goes only after the list was loaded, so a failed GET never wipes the links.
 */
export const buildBasicDataPayload = (form: BasicDataForm): BasicDataPayload => {
    const payload: BasicDataPayload = {
        id: form.id,
        uuid: form.personId,
        govTaxId: cleanTaxId(form.govTaxId),
        legalName: form.legalName,
        fantasyName: form.fantasyName,
        zipcode: form.zipcode.replace(/\D/g, ''),
        streetType: form.streetType || '',
        street: form.street || '',
        number: form.number || '',
        complement: form.complement || '',
        district: form.district || '',
        cityId: form.cityId?.toString() || '',
        nire: (form.nire || '').replace(/\D/g, ''),
        registrationStatus: form.registrationStatus || 'IN_NEGOTIATION',
    };
    if (form.financialRepresentativesLoaded !== true) return payload;
    return { ...payload, financialRepresentativeIds: toFinancialRepresentativeIds(form.financialRepresentatives ?? []) };
};

/**
 * True when the basic data differ from the last saved payload or a new profile is still pending.
 * The Financial Representatives count as a set (order does not matter) and only when both sides have them.
 */
export const hasUnsavedChanges = (
    savedPayload: BasicDataPayload | null,
    form: BasicDataForm,
    profiles: string[],
    dbProfiles: string[],
): boolean => {
    const current = buildBasicDataPayload(form);
    return JSON.stringify(withoutFinancialRepresentativeIds(current)) !== JSON.stringify(withoutFinancialRepresentativeIds(savedPayload))
        || financialRepresentativesChanged(savedPayload, current)
        || buildNewProfilesRequest(profiles, dbProfiles).profiles.length > 0;
};

/**
 * The saved snapshot with the Financial Representative ids that arrived later; the other saved
 * keys are kept, so a field edited meanwhile still counts as unsaved. A null snapshot stays null.
 */
export const withSavedFinancialRepresentativeIds = (
    saved: BasicDataPayload | null,
    ids: string[],
): BasicDataPayload | null => {
    if (saved === null) return null;
    return { ...saved, financialRepresentativeIds: ids };
};

/**
 * "Notificar cadastro atualizado" is enabled only for a saved COMPLETED status without unsaved changes.
 * The saved status decides the reason first (requirement 2.4).
 */
export const canNotifyRegistrationUpdated = (savedStatus: string | undefined, unsaved: boolean): NotifyButtonState => {
    if (savedStatus !== COMPLETED_REGISTRATION_STATUS) return { enabled: false, reason: SAVED_STATUS_NOT_COMPLETED_REASON };
    if (unsaved) return { enabled: false, reason: UNSAVED_CHANGES_REASON };
    return { enabled: true, reason: '' };
};
